import { useEffect, useState } from "react";
import Icon from "@hackclub/icons";
import type { DraftTimelapse, KnownDevice } from "@hackclub/lapse-api";
import * as bip39 from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";
import { fromHex } from "@hackclub/lapse-shared";

import { api } from "@/api";
import { deviceStorage, LocalDevice } from "@/deviceStorage";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { PasskeyModal } from "@/components/layout/PasskeyModal";
import { WindowedModal } from "@/components/layout/WindowedModal";

export function DevicesTab({ isVisible }: { isVisible: boolean }) {
  const auth = useAuth(false);

  const [wordsVisible, setWordsVisible] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);

  const [currentDeviceForPin, setCurrentDeviceForPin] = useState<string | null>(null);
  const [deviceToRemove, setDeviceToRemove] = useState<string | null>(null);
  const [removeDeviceModalOpen, setRemoveDeviceModalOpen] = useState(false);
  const [timelapsesToRemove, setTimelapsesToRemove] = useState<DraftTimelapse[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [devices, setDevices] = useState<KnownDevice[]>([]);
  const [localDevices, setLocalDevices] = useState<LocalDevice[]>([]);
  const [lookoutDevices, setLookoutDevices] = useState<Array<{ id: string; label: string; createdAt: string; lastUsedAt: string | null }>>([]);
  const [lookoutDevicesLoading, setLookoutDevicesLoading] = useState(true);

  useEffect(() => {
    if (!isVisible) return;

    (async () => {
      setLocalDevices(await deviceStorage.getAllDevices());

      const res = await api.user.getDevices({});
      if (res.ok) {
        setDevices(res.data.devices);
      }

      const lookoutRes = await api.timelapse.listDesktopDevices({});
      if (lookoutRes.ok) {
        setLookoutDevices(lookoutRes.data.devices);
      }
      setLookoutDevicesLoading(false);
    })();
  }, [isVisible]);

  async function revokeLookoutDevice(id: string) {
    const res = await api.timelapse.revokeDesktopDevice({ id });
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setLookoutDevices(prev => prev.filter(d => d.id !== id));
  }

  async function handleRemoveDevice(deviceId: string) {
    if (!auth.currentUser) return;

    const res = await api.draftTimelapse.findByUser({ user: auth.currentUser.id });
    if (!res.ok) {
      setError(res.message);
      return;
    }

    const timelapses = res.data.timelapses;

    if (timelapses.length > 0) {
      setDeviceToRemove(deviceId);
      setTimelapsesToRemove(timelapses);
      setRemoveDeviceModalOpen(true);
    } else {
      await confirmRemoveDevice(deviceId);
    }
  }

  async function confirmRemoveDevice(deviceId: string) {
    const req = await api.user.removeDevice({ id: deviceId });
    if (!req.ok) {
      setError(req.message);
      return;
    }

    await deviceStorage.deleteDevice(deviceId);
    setDevices(devices.filter(device => device.id !== deviceId));
    setRemoveDeviceModalOpen(false);
    setDeviceToRemove(null);
    setTimelapsesToRemove([]);
  }

  function handleAddPasskey(deviceId: string) {
    setCurrentDeviceForPin(deviceId);
    setPinModalOpen(true);
  }

  async function handleRemovePasskey(deviceId: string) {
    await deviceStorage.deleteDevice(deviceId);
    setLocalDevices(await deviceStorage.getAllDevices());
  }

  async function handlePinSubmit(passkey: string) {
    if (currentDeviceForPin) {
      await deviceStorage.saveDevice({
        id: currentDeviceForPin,
        passkey: passkey,
        thisDevice: false
      });
      setLocalDevices(await deviceStorage.getAllDevices());
    }
  }

  const currentPasskey = localDevices.find(d => d.thisDevice)?.passkey || null;
  const callingDeviceId = localDevices.find(d => d.thisDevice)?.id || null;

  let deviceWords: string | null = null;
  if (currentPasskey) {
    try {
      deviceWords = bip39.entropyToMnemonic(fromHex(currentPasskey), wordlist);
    } catch {
      deviceWords = null;
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl border border-slate bg-darkless px-4 py-3 text-muted">
        With Lookout, device keys are no longer needed. These settings only apply to older timelapses recorded with the legacy encrypted pipeline.
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold">Linked apps</h3>
          <p className="text-muted">
            The Lookout desktop app installs you have approved. Revoke to stop an install from starting timelapses on your account - it will have to be approved again from the app.
          </p>
        </div>

        {lookoutDevicesLoading ? (
          <p className="text-muted text-center">Loading...</p>
        ) : lookoutDevices.length === 0 ? (
          <p className="text-muted text-center">No linked apps.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {lookoutDevices.map(device => (
              <div key={device.id} className="flex items-center justify-between p-3 border-l-8 border-primary rounded-md">
                <div className="flex flex-col min-w-0">
                  <span className="font-medium truncate">{device.label}</span>
                  <span className="text-sm text-muted">
                    Linked {new Date(device.createdAt).toLocaleDateString()}
                    {device.lastUsedAt && ` · last used ${new Date(device.lastUsedAt).toLocaleDateString()}`}
                  </span>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button kind="regular" onClick={() => revokeLookoutDevice(device.id)} className="px-5!" title="Revoke">
                    <Icon glyph="delete" size={20} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col">
          <h3 className="font-bold">Device Key</h3>
          <p className="text-muted">Click to show! You&apos;ll need these words to access unpublished timelapses on other devices.</p>
        </div>

        <div className="w-full flex justify-center">
          <button
            type="button"
            className="border border-slate w-full flex justify-center rounded-md p-3 px-8 cursor-pointer hover:bg-darker transition-colors shadow"
            onClick={() => setWordsVisible(!wordsVisible)}
          >
            <span className={`font-mono text-sm leading-relaxed select-none transition-all text-center ${wordsVisible ? "" : "blur-xs"}`}>
              {deviceWords || "no key found"}
            </span>
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold">Known Devices</h3>
          <p className="text-muted">In order to access unpublished timelapses from another device, you&apos;ll need its key.</p>
        </div>

        {devices.length === 0 ? (
          <p className="text-muted text-center">No devices found</p>
        ) : (
          <div className="flex flex-col gap-4">
            {devices.map(device => (
              <div key={device.id} className="flex items-center justify-between p-3 border-l-8 border-primary rounded-md">
                <div className="flex flex-col">
                  <span className="font-medium">{device.name}</span>
                  <span className="text-sm text-muted">{device.id}</span>
                </div>
                <div className="flex gap-2">
                  {localDevices.some(d => d.id === device.id && d.thisDevice) ? (
                    <span>(this device)</span>
                  ) : (
                    <>
                      {localDevices.some(x => x.id === device.id) ? (
                        <Button kind="regular" onClick={() => handleRemovePasskey(device.id)} className="p-2">
                          <Icon glyph="private" size={16} />
                          <span>Remove key</span>
                        </Button>
                      ) : (
                        <Button kind="regular" onClick={() => handleAddPasskey(device.id)} className="p-2">
                          <Icon glyph="private" size={16} />
                          <span>Add key</span>
                        </Button>
                      )}

                      <Button kind="regular" onClick={() => handleRemoveDevice(device.id)} className="px-5!">
                        <Icon glyph="delete" size={20} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <p className="text-red">{error}</p>}

      {currentDeviceForPin && callingDeviceId && (
        <PasskeyModal
          isOpen={pinModalOpen}
          setIsOpen={setPinModalOpen}
          description={`Transfer the key for ${devices.find(d => d.id === currentDeviceForPin)?.name || "Unknown Device"}`}
          targetDeviceId={currentDeviceForPin}
          callingDeviceId={callingDeviceId}
          onPasskeySubmit={handlePinSubmit}
        />
      )}

      <WindowedModal
        icon="delete"
        title="Remove Device"
        description="This action cannot be undone"
        isOpen={removeDeviceModalOpen}
        setIsOpen={setRemoveDeviceModalOpen}
      >
        <div className="flex flex-col gap-4">
          <p>
            This device has {timelapsesToRemove.length} unpublished timelapse{timelapsesToRemove.length !== 1 ? "s" : ""} that will be permanently deleted:
          </p>

          <div className="bg-darkless rounded-md p-3 px-4 max-h-32 overflow-y-auto">
            {timelapsesToRemove.map(timelapse => (
              <div key={timelapse.id} className="flex justify-between items-center py-1">
                <div className="flex flex-col">
                  <span className="font-bold truncate">{timelapse.name}</span>
                  <span className="truncate text-muted">{timelapse.description}</span>
                </div>

                <span className="text-sm text-muted ml-2">{timelapse.id.slice(0, 8)}...</span>
              </div>
            ))}
          </div>

          <div className="flex gap-4">
            <Button
              kind="primary"
              onClick={() => deviceToRemove && confirmRemoveDevice(deviceToRemove)}
              className="flex-1"
            >
              Remove Device
            </Button>

            <Button
              kind="regular"
              onClick={() => { setRemoveDeviceModalOpen(false); setDeviceToRemove(null); setTimelapsesToRemove([]); }}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </WindowedModal>
    </div>
  );
}
